import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft } from 'lucide-react';
import { specialEventsService, eventsService } from '../../services';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { LoadingPage } from '../../components/ui/Loading';

const SpecialEventForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    eventType: 'BC',
    eventDate: '',
    linkedEventId: '',
    status: 'DRAFT',
    maxPartySize: 6,
    requireBuff: true
  });
  const [normalEvents, setNormalEvents] = useState([]);

  useEffect(() => {
    if (isEditing) {
      loadEvent();
    }
  }, [id]);

  useEffect(() => {
    eventsService.getEvents({ limit: 200 })
      .then(data => setNormalEvents(data.events || []))
      .catch(() => setNormalEvents([]));
  }, []);

  const loadEvent = async () => {
    try {
      setLoading(true);
      const data = await specialEventsService.getById(id);
      const event = data.event;
      
      setFormData({
        name: event.name,
        description: event.description || '',
        eventType: event.eventType,
        eventDate: event.eventDate ? event.eventDate.split('T')[0] : '',
        linkedEventId: event.linkedEventId || '',
        status: event.status,
        maxPartySize: event.maxPartySize,
        requireBuff: event.requireBuff
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar evento');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');

      if (isEditing) {
        await specialEventsService.update(id, formData);
      } else {
        await specialEventsService.create(formData);
      }

      navigate('/app/admin/special-events');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao salvar evento');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading && isEditing) {
    return <LoadingPage />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/app/admin/special-events')}
              className="flex items-center justify-center w-10 h-10 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-orange-500/50 rounded-lg text-gray-300 hover:text-white transition-all duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent mb-1">
                {isEditing ? '✏️ Editar Evento Especial' : '➕ Novo Evento Especial'}
              </h1>
              <p className="text-gray-300">
                Configure os detalhes do evento
              </p>
            </div>
          </div>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit}>
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
            <div className="space-y-6">
            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4">
                <p className="text-red-400">{error}</p>
              </div>
            )}

            {/* Nome */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Nome do Evento *
              </label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Ex: BC Semanal - 15/12"
                required
              />
            </div>

            {/* Descrição */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Descrição
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                className="w-full bg-white/5 text-white rounded-xl px-4 py-3 border border-white/10 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 focus:outline-none transition-all"
                placeholder="Detalhes sobre o evento..."
              />
            </div>

            {/* Tipo e Data */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Tipo *
                </label>
                <Select
                  name="eventType"
                  value={formData.eventType}
                  onChange={handleChange}
                  required
                >
                  <option value="PVP">PVP</option>
                  <option value="BC">BC</option>
                  <option value="RAID">Raid</option>
                  <option value="DUNGEON">Dungeon</option>
                  <option value="CUSTOM">Personalizado</option>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Data do Evento
                </label>
                <Input
                  type="date"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Evento normal vinculado */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Evento normal vinculado
              </label>
              <Select
                name="linkedEventId"
                value={formData.linkedEventId}
                onChange={handleChange}
              >
                <option value="">Nenhum</option>
                {normalEvents.map(ev => (
                  <option key={ev.id} value={ev.id}>{ev.name}</option>
                ))}
              </Select>
              <p className="text-xs text-slate-400 mt-1">
                Usado no pagamento BC para conferir as provas de participação. É preenchido automaticamente ao "Importar de evento".
              </p>
            </div>

            {/* Status e Max Party Size */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Status *
                </label>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  <option value="DRAFT">Rascunho</option>
                  <option value="ACTIVE">Ativo</option>
                  <option value="COMPLETED">Concluído</option>
                  <option value="CANCELLED">Cancelado</option>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Máximo por Grupo *
                </label>
                <Input
                  type="number"
                  name="maxPartySize"
                  value={formData.maxPartySize}
                  onChange={handleChange}
                  min="1"
                  max="10"
                  required
                />
              </div>
            </div>

            {/* Require Buff */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                name="requireBuff"
                checked={formData.requireBuff}
                onChange={handleChange}
                className="w-5 h-5 rounded bg-white/5 border-white/10 text-orange-500 focus:ring-2 focus:ring-orange-500/20 focus:outline-none"
              />
              <label className="text-sm text-slate-300">
                Exigir pelo menos 1 buff por grupo
              </label>
            </div>

            {/* Botões */}
            <div className="flex gap-3 pt-4 border-t border-white/10">
              <Button
                type="button"
                onClick={() => navigate('/app/admin/special-events')}
                className="bg-white/5 hover:bg-white/10 border border-white/10"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 shadow-lg shadow-orange-500/20"
              >
                <Save className="w-4 h-4" />
                {loading ? 'Salvando...' : 'Salvar Evento'}
              </Button>
            </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SpecialEventForm;
